import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Auth_SignUpInput, EmailVerificationType, useAuth_SignUpMutation } from 'queries';
import { Eye } from 'ts-react-feather-icons';
import * as yup from 'yup';

import FormPhoneField from 'common/form-phone-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import Acknowledgement from './acknowledgement';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '5vh',
    paddingLeft: '12vw',
    paddingRight: '12vw',
  },
  inputWrapper: {
    padding: '1vh 1vh 1vh 1vh',
  },
  iconButton: {
    padding: '6px',
  },
  input: {
    display: 'flex',
    margin: 'auto',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  bottomAlign: {
    marginTop: 'auto',
    bottom: '0',
  },
  warning: {
    display: 'block',
    color: 'red',
    fontSize: '.65rem',
    lineHeight: '1.3',
  },
}));

const schema = yup.object().shape({
  firstName: yup.string().required('Please enter a first name'),
  lastName: yup.string().required('Please enter a last name'),
  email: yup.string().required('Please enter an email').email(),
  phone: yup.string().required('Please enter a phone number'),
  password: yup
    .string()
    .required('Please enter a password')
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&_-])[A-Za-z\d@$!%*#?&_-]{8,}$/,
      'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
    ),
  confirmPassword: yup
    .string()
    .required()
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

const IndividualForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const [showAcknowledgement, setShowAcknowledgement] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm<Auth_SignUpInput>({ resolver: yupResolver(schema) });

  const [signup] = useAuth_SignUpMutation({
    onCompleted: (data) => {
      const output = JSON.parse(data.data.output);
      history.push({
        pathname: '/verify-email/' + output.emailVerificationId,
        state: {
          verificationType: EmailVerificationType.UserRegistration,
        },
      });
    },
    onError: (e) => {
      showAlert({
        message: 'Error resetting password: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
      setShowAcknowledgement(false);
    },
  });

  const signUpUser = async () => {
    const formData = getValues();
    await signup({ variables: { input: formData } });
  };

  const submitForm = async () => {
    setShowAcknowledgement(true);
  };

  return (
    <>
      <Acknowledgement
        open={showAcknowledgement}
        onAccept={signUpUser}
        onCancel={() => setShowAcknowledgement(false)}
      />
      <form className={classes.root} onSubmit={handleSubmit(submitForm)}>
        <div className={classes.inputWrapper}>
          <Controller
            name="firmName"
            control={control}
            render={({ field: { ref, name, onBlur, onChange, value } }) => (
              <FormTextField
                name={name}
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                errorMessage={errors.firmName?.message}
                label="Firm Name"
                className={classes.input}
                data-cy="sign-up-firm-name"
              />
            )}
          />
        </div>
        <Grid container>
          <Grid xs={12} item md={6}>
            <div className={classes.inputWrapper}>
              <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, name, onBlur, onChange, value } }) => (
                  <FormTextField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    errorMessage={errors.firstName?.message}
                    label="First Name"
                    className={clsx(classes.input, errors.firstName && classes.inputError)}
                    data-cy="sign-up-first"
                  />
                )}
              />
            </div>
          </Grid>
          <Grid xs={12} item md={6}>
            <div className={classes.inputWrapper}>
              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, name, onBlur, onChange, value } }) => (
                  <FormTextField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    errorMessage={errors.lastName?.message}
                    label="Last Name"
                    className={clsx(classes.input, errors.lastName && classes.inputError)}
                    data-cy="sign-up-last"
                  />
                )}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container>
          <Grid xs={12} item md={7} className={classes.bottomAlign}>
            <div className={classes.inputWrapper}>
              <Controller
                name="email"
                control={control}
                render={({ field: { ref, name, onBlur, onChange, value } }) => (
                  <FormTextField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    errorMessage={errors.email?.message}
                    label="Email"
                    className={clsx(classes.input, errors.email && classes.inputError)}
                    data-cy="sign-up-email"
                  />
                )}
              />
            </div>
          </Grid>
          <Grid xs={12} item md={5}>
            <div className={classes.inputWrapper}>
              <Typography>Mobile Phone</Typography>
              <Typography variant="caption" className={classes.warning}>
                Do not use a land-line. Must be a number that can receive text messages for security
                verification purposes.
              </Typography>
              <Controller
                name="phone"
                control={control}
                render={({ field: { name, onBlur, onChange, value } }) => (
                  <FormPhoneField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.phone?.message}
                    data-cy="sign-up-phone"
                  />
                )}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container>
          <Grid xs={12} item md={6}>
            <div className={classes.inputWrapper}>
              <Controller
                name="password"
                control={control}
                render={({ field: { ref, name, onBlur, onChange, value } }) => (
                  <FormTextField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    type={showPassword ? 'text' : 'password'}
                    errorMessage={errors.password?.message}
                    label="Password"
                    className={clsx(classes.input, errors.password && classes.inputError)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => setShowPassword(!showPassword)}
                          size="large"
                        >
                          <Eye />
                        </IconButton>
                      ),
                    }}
                    data-cy="sign-up-password"
                  />
                )}
              />
            </div>
          </Grid>
          <Grid xs={12} item md={6}>
            <div className={classes.inputWrapper}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field: { ref, name, onBlur, onChange, value } }) => (
                  <FormTextField
                    name={name}
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    type="password"
                    errorMessage={errors.confirmPassword?.message}
                    label="Confirm Password"
                    className={clsx(classes.input, errors.confirmPassword && classes.inputError)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => setShowPassword(!showPassword)}
                          size="large"
                        >
                          <Eye />
                        </IconButton>
                      ),
                    }}
                    data-cy="sign-up-password-confirm"
                  />
                )}
              />
            </div>
          </Grid>
        </Grid>
        <div style={{ textAlign: 'center', marginTop: '50px', marginBottom: '30px' }}>
          <Button type="submit" variant="contained" color="secondary">
            Next
          </Button>
        </div>
      </form>
    </>
  );
};

export default IndividualForm;
